import React, { useState, useEffect, useRef, useCallback } from "react";
import { SharedTextInput, SharedButton, SharedFeatureError } from "../../../shared";
import { failedMessage } from "../../../shared/serverReason";
import {
  useGetMyConnectProfileQuery,
  useUpsertConnectProfileMutation,
} from "../../../../store/slices/api";
import { selectUser } from "../../../../store/slices/authentication";
import { useSelector } from "react-redux";
import ImageCropper from "./ImageCropper";
import ProfileCard from "../Directory/ProfileCard";

// Reads a file from disk and returns it as a data URL (no resizing yet)
/**
 * Read a file and resize it so the longest side is at most MAX_PX.
 * This ensures even huge phone photos load instantly in the cropper
 * and the final data URL stays small enough to store.
 */
const MAX_RESIZE_PX = 1200;
const readAndResizeFile = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = (e) => {
      const img = new Image();
      img.onerror = reject;
      img.onload = () => {
        const { width, height } = img;
        // If already small enough, return as-is
        if (width <= MAX_RESIZE_PX && height <= MAX_RESIZE_PX) {
          resolve(e.target.result);
          return;
        }
        // Scale down so the longest side is MAX_RESIZE_PX
        const ratio = MAX_RESIZE_PX / Math.max(width, height);
        const newW = Math.round(width * ratio);
        const newH = Math.round(height * ratio);
        const canvas = document.createElement("canvas");
        canvas.width = newW;
        canvas.height = newH;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, newW, newH);
        resolve(canvas.toDataURL("image/jpeg", 0.9));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });

const MAX_PREFERRED_NAME_LENGTH = 50;
const MAX_EMAIL_LENGTH = 100;
const MAX_PHONE_LENGTH = 20;
const MAX_STATE_LENGTH = 50;
const MAX_JOB_LENGTH = 100;
const MAX_DESCRIPTION_LENGTH = 500;
const MAX_PICTURE_URL_LENGTH = 500;

const TOTAL_TOGGLES = 8;

/* ── Small toggle switch used inside the Social Settings panel ──
   A real switch button inside the label, so Tab reaches it, Space flips it, a
   screen reader says "on" or "off", and a click anywhere on the row flips it. */
function SharingToggle({ label, helperText, checked, onChange }) {
  return (
    <label className="flex items-center justify-between py-[10px] cursor-pointer">
      <div className="flex flex-col gap-y-[2px]">
        <span className="text-[17px] font-display font-bold text-dupontGold">
          {label}
        </span>
        {helperText && (
          <span className="text-[14px] font-body text-dupontCream/90">
            {helperText}
          </span>
        )}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        className={`relative w-[44px] h-[24px] rounded-full transition-colors ${
          checked ? "bg-dupontGold" : "bg-dupontCream/30"
        }`}
        onClick={() => onChange(!checked)}
      >
        <span
          className={`absolute top-[2px] left-0 w-[20px] h-[20px] rounded-full bg-white shadow transition-transform ${
            checked ? "translate-x-[22px]" : "translate-x-[2px]"
          }`}
        />
      </button>
    </label>
  );
}

function MyProfile() {
  const user = useSelector(selectUser);
  const individualId = parseInt(user.externalId);

  const {
    data: existingProfile,
    isFetching: isFetchingProfile,
    isError: profileLoadFailed,
    refetch: refetchProfile,
  } = useGetMyConnectProfileQuery(individualId);
  const [upsertProfile, { isLoading: isSaving }] =
    useUpsertConnectProfileMutation();

  const fileInputRef = useRef(null);
  const closeupFileInputRef = useRef(null);
  const [profilePictureUrl, setProfilePictureUrl] = useState("");
  const [closeupPictureUrl, setCloseupPictureUrl] = useState("");
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [isUploadingCloseup, setIsUploadingCloseup] = useState(false);
  const [rawImage, setRawImage] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [preferredName, setPreferredName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [stateOfResidency, setStateOfResidency] = useState("");
  const [jobOrPassion, setJobOrPassion] = useState("");
  const [description, setDescription] = useState("");
  const [isVisible, setIsVisible] = useState(true);
  const [allowMessages, setAllowMessages] = useState(true);
  const [showReadReceipts, setShowReadReceipts] = useState(true);
  const [showEmail, setShowEmail] = useState(true);
  const [showPhone, setShowPhone] = useState(true);
  const [showState, setShowState] = useState(true);
  const [showJob, setShowJob] = useState(true);
  const [showDescription, setShowDescription] = useState(true);
  const [showPreferredName, setShowPreferredName] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  // Pre-fill form if profile exists
  useEffect(() => {
    if (existingProfile) {
      setProfilePictureUrl(existingProfile.profile_picture_url || "");
      setCloseupPictureUrl(existingProfile.closeup_picture_url || "");
      setPreferredName(existingProfile.preferred_name || "");
      setEmail(existingProfile.email || "");
      setPhoneNumber(existingProfile.phone_number || "");
      setStateOfResidency(existingProfile.state_of_residency || "");
      setJobOrPassion(existingProfile.job_or_passion || "");
      setDescription(existingProfile.description || "");
      setIsVisible(existingProfile.is_visible ?? true);
      setAllowMessages(existingProfile.allow_messages ?? true);
      setShowReadReceipts(existingProfile.show_read_receipts ?? true);
      setShowEmail(existingProfile.show_email ?? true);
      setShowPhone(existingProfile.show_phone ?? true);
      setShowState(existingProfile.show_state ?? true);
      setShowJob(existingProfile.show_job ?? true);
      setShowDescription(existingProfile.show_description ?? true);
      setShowPreferredName(existingProfile.show_preferred_name ?? true);
    }
  }, [existingProfile]);

  const togglesOnCount = [
    showPreferredName,
    showEmail,
    showPhone,
    showState,
    showJob,
    showDescription,
    allowMessages,
    showReadReceipts,
  ].filter(Boolean).length;

  // Called when the user finishes cropping — image is already sized by the cropper
  const handleCropComplete = useCallback((croppedDataUrl) => {
    setShowCropper(false);
    setRawImage(null);
    setProfilePictureUrl(croppedDataUrl);
  }, []);

  const handleCropCancel = useCallback(() => {
    setShowCropper(false);
    setRawImage(null);
  }, []);

  const handleSave = async () => {
    if (isSaving) return;
    setSaved(false);
    setSaveError(null);
    // Only say "saved" once the server has it. On failure everything typed
    // stays in the form, and the member is told it didn't save.
    try {
      await upsertProfile({
        individual_id: individualId,
        profile_picture_url: profilePictureUrl.trim() || null,
        closeup_picture_url: closeupPictureUrl.trim() || null,
        preferred_name: preferredName.trim() || null,
        email: email.trim() || null,
        phone_number: phoneNumber.trim() || null,
        state_of_residency: stateOfResidency.trim() || null,
        job_or_passion: jobOrPassion.trim() || null,
        description: description.trim() || null,
        is_visible: isVisible,
        allow_messages: allowMessages,
        show_read_receipts: showReadReceipts,
        show_email: showEmail,
        show_phone: showPhone,
        show_state: showState,
        show_job: showJob,
        show_description: showDescription,
        show_preferred_name: showPreferredName,
      }).unwrap();
    } catch (err) {
      setSaveError(
        failedMessage(err, "Your profile didn't save. Everything you typed is still here — please try again.")
      );
      return;
    }
    setSaved(true);
    setShowPreview(true);
    setTimeout(() => setSaved(false), 3000);
  };

  // Build a mock profile that mirrors what others would see
  const previewProfile = {
    name: user.fullName || "You",
    profile_picture_url: profilePictureUrl.trim() || null,
    preferred_name: showPreferredName ? preferredName.trim() || null : null,
    email: showEmail ? email.trim() || null : null,
    phone_number: showPhone ? phoneNumber.trim() || null : null,
    state_of_residency: showState ? stateOfResidency.trim() || null : null,
    job_or_passion: showJob ? jobOrPassion.trim() || null : null,
    description: showDescription ? description.trim() || null : null,
  };

  // Until the saved profile has arrived (null = "no profile yet" is a real
  // answer), there is no form: it would show blank fields and every sharing
  // switch "on", and saving it would wipe the real profile. That includes a
  // "Try again" in flight, when the query reports neither loading nor error.
  // (A failed reload after it HAS loaded keeps the form, so nothing typed is
  // lost.)
  if (existingProfile === undefined) {
    if (profileLoadFailed && !isFetchingProfile) {
      return (
        <SharedFeatureError
          title="We couldn't load your profile"
          message="Nothing has been changed. Please try again in a moment."
          onRetry={refetchProfile}
        />
      );
    }
    return (
      <div className="text-center text-dupontBlue dark:text-darkText py-[40px]">
        Loading your profile...
      </div>
    );
  }

  return (
    <div className="max-w-[700px] mx-auto flex flex-col gap-y-[24px]">
      {/* Explanation banner */}
      <div className="rounded-[12px] bg-dupontBlue/5 dark:bg-darkGold/[0.06] border border-dupontBlue/20 dark:border-darkGold/10 p-[20px]">
        <p className="text-[15px] font-body text-dupontBlue dark:text-darkText leading-relaxed">
          <span className="font-bold">How sharing works:</span> Each field has
          its own on/off toggle in Social Settings below. You'll only see a
          cousin's field when both of you have that field's toggle turned on
          — and they'll only see yours when you do too. Turn on what you're
          comfortable sharing.
        </p>
      </div>

      {/* Toggle counter */}
      <div className="text-[14px] font-body text-dupontBlue/70 dark:text-darkTextSecondary">
        You have{" "}
        <span className="font-bold text-dupontBlue dark:text-darkText">
          {togglesOnCount} of {TOTAL_TOGGLES}
        </span>{" "}
        fields turned on in Social Settings.
      </div>

      {/* Form */}
      <div className="flex flex-col rounded-[16px] shadow-xl dark:shadow-[0_8px_30px_rgba(0,0,0,0.3)] p-[32px] border border-dupontGold bg-white dark:bg-darkCard gap-y-[16px]">
        <div className="text-[18px] font-bold text-dupontBlue dark:text-darkText">
          {existingProfile ? "Edit Your Profile" : "Create Your Profile"}
        </div>

        {/* Profile picture */}
        <div className="flex flex-col gap-y-[10px]">
          <div className="text-[14px] font-bold text-dupontBlue dark:text-darkText">
            Profile Picture
          </div>

          {/* Preview */}
          {profilePictureUrl && (
            <div className="flex items-center gap-x-[16px]">
              <img
                src={profilePictureUrl}
                alt="Profile preview"
                className="w-[100px] h-[100px] rounded-full object-cover border-2 border-dupontGold"
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
              <button
                type="button"
                className="text-[13px] text-red-500 cursor-pointer underline"
                onClick={() => setProfilePictureUrl("")}
              >
                Remove photo
              </button>
            </div>
          )}

          {/* Upload from computer */}
          <div className="flex flex-row items-center gap-x-[12px]">
            <input
              type="file"
              accept="image/*"
              ref={fileInputRef}
              className="hidden"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                setIsUploadingPhoto(true);
                try {
                  const dataUrl = await readAndResizeFile(file);
                  setRawImage(dataUrl);
                  setShowCropper(true);
                } catch (err) {
                  console.error("Failed to read image:", err);
                } finally {
                  setIsUploadingPhoto(false);
                  if (fileInputRef.current) fileInputRef.current.value = "";
                }
              }}
            />
            <SharedButton
              color="secondary"
              onClick={() => fileInputRef.current?.click()}
              loadingText={isUploadingPhoto ? "Processing..." : undefined}
            >
              Upload from Computer
            </SharedButton>
            <span className="text-[13px] text-dupontBlue/40 dark:text-darkTextTertiary">or</span>
          </div>

          {/* Paste URL */}
          <SharedTextInput
            label="Paste a link to your photo"
            name="profilePictureUrl"
            value={profilePictureUrl.startsWith("data:") ? "" : profilePictureUrl}
            onChange={setProfilePictureUrl}
            variant="outlined"
            maxChars={MAX_PICTURE_URL_LENGTH}
            placeholder="https://example.com/my-photo.jpg"
          />

          <div className="text-[13px] text-dupontBlue/50 dark:text-darkTextTertiary">
            Upload a photo from your computer or paste a link. Your picture is
            always visible on your profile card.
          </div>
        </div>

        {/* ── Profile Close Up ── */}
        <div className="flex flex-col gap-y-[12px] p-[20px] rounded-[12px] border border-dupontGold/30 dark:border-darkGold/20 bg-dupontBlue/[0.02] dark:bg-darkGold/[0.03]">
          <h3 className="text-[18px] font-display font-bold text-dupontBlue dark:text-darkText">
            Profile Close Up
          </h3>
          <p className="text-[14px] font-body text-dupontBlue/70 dark:text-darkText/70">
            This is a larger photo that cousins see when they click on your profile picture.
            Great for a clear, face-forward shot so family can recognize you!
          </p>

          {closeupPictureUrl && (
            <div className="flex justify-center">
              <img
                src={closeupPictureUrl}
                alt="Close up preview"
                className="max-w-[min(280px,100%)] max-h-[280px] rounded-[12px] border-2 border-dupontGold/40 object-cover"
              />
            </div>
          )}

          {/* Wraps on the smallest phones instead of pushing the page sideways. */}
          <div className="flex flex-row flex-wrap items-center gap-x-[12px] gap-y-[8px]">
            <input
              type="file"
              accept="image/*"
              ref={closeupFileInputRef}
              className="hidden"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                setIsUploadingCloseup(true);
                try {
                  const dataUrl = await readAndResizeFile(file);
                  setCloseupPictureUrl(dataUrl);
                } catch (err) {
                  console.error("Failed to read image:", err);
                } finally {
                  setIsUploadingCloseup(false);
                  if (closeupFileInputRef.current) closeupFileInputRef.current.value = "";
                }
              }}
            />
            <SharedButton
              color="secondary"
              onClick={() => closeupFileInputRef.current?.click()}
              loadingText={isUploadingCloseup ? "Processing..." : undefined}
            >
              {closeupPictureUrl ? "Change Close Up" : "Upload Close Up"}
            </SharedButton>
            {closeupPictureUrl && (
              <button
                type="button"
                onClick={() => setCloseupPictureUrl("")}
                className="text-[13px] text-red-500 hover:text-red-700 font-medium"
              >
                Remove
              </button>
            )}
          </div>
        </div>

        <SharedTextInput
          label="Preferred Name / Nickname"
          name="preferredName"
          value={preferredName}
          onChange={setPreferredName}
          variant="outlined"
          maxChars={MAX_PREFERRED_NAME_LENGTH}
          placeholder="e.g. Sam, CJ, Aunt Mae"
        />

        <SharedTextInput
          label="Email"
          name="email"
          value={email}
          onChange={setEmail}
          variant="outlined"
          maxChars={MAX_EMAIL_LENGTH}
          placeholder="cousin@example.com"
        />

        <SharedTextInput
          label="Phone Number"
          name="phoneNumber"
          value={phoneNumber}
          onChange={setPhoneNumber}
          variant="outlined"
          maxChars={MAX_PHONE_LENGTH}
          placeholder="(555) 123-4567"
        />

        <SharedTextInput
          label="State / Region"
          name="stateOfResidency"
          value={stateOfResidency}
          onChange={setStateOfResidency}
          variant="outlined"
          maxChars={MAX_STATE_LENGTH}
          placeholder="e.g. Delaware, or Tuscany, Italy"
        />

        <SharedTextInput
          label="Job or Passion"
          name="jobOrPassion"
          value={jobOrPassion}
          onChange={setJobOrPassion}
          variant="outlined"
          maxChars={MAX_JOB_LENGTH}
          placeholder="e.g. Teacher, Sailing enthusiast"
        />

        <SharedTextInput
          label="About You"
          name="description"
          value={description}
          onChange={setDescription}
          multiline
          rows={3}
          variant="outlined"
          maxChars={MAX_DESCRIPTION_LENGTH}
          placeholder="A short description about yourself..."
        />

        {/* ── Social Settings / Sharing Preferences (collapsible dark/gold panel) ── */}
        <div className="rounded-[12px] overflow-hidden border border-dupontGold/40">
          <button
            type="button"
            aria-expanded={settingsOpen}
            className="flex w-full items-center justify-between px-[16px] py-[12px] bg-dupontBlue cursor-pointer select-none text-left"
            onClick={() => setSettingsOpen((o) => !o)}
          >
            <span className="text-[17px] font-display font-bold text-dupontGold">
              Social Settings
            </span>
            <span className="text-dupontGold text-[18px]" aria-hidden="true">
              {settingsOpen ? "\u25B2" : "\u25BC"}
            </span>
          </button>

          {settingsOpen && (
            <div className="bg-dupontBlue/95 px-[16px] divide-y divide-dupontCream/10">
              <SharingToggle
                label="Preferred Name"
                helperText="Let cousins see your nickname"
                checked={showPreferredName}
                onChange={setShowPreferredName}
              />
              <SharingToggle
                label="Email"
                helperText="Let cousins see your email address"
                checked={showEmail}
                onChange={setShowEmail}
              />
              <SharingToggle
                label="Phone"
                helperText="Let cousins see your phone number"
                checked={showPhone}
                onChange={setShowPhone}
              />
              <SharingToggle
                label="State / Region"
                helperText="Let cousins see where you live"
                checked={showState}
                onChange={setShowState}
              />
              <SharingToggle
                label="Job / Passion"
                helperText="Let cousins see what you do"
                checked={showJob}
                onChange={setShowJob}
              />
              <SharingToggle
                label="About You"
                helperText="Let cousins see your description"
                checked={showDescription}
                onChange={setShowDescription}
              />
              <SharingToggle
                label="Allow Messages"
                helperText="Let cousins send you messages"
                checked={allowMessages}
                onChange={setAllowMessages}
              />
              <SharingToggle
                label="Read Receipts"
                helperText="Let cousins know when you've read their messages"
                checked={showReadReceipts}
                onChange={setShowReadReceipts}
              />
            </div>
          )}
        </div>

        {/* Visibility toggle */}
        <label className="flex items-center gap-x-[10px] cursor-pointer">
          <input
            type="checkbox"
            checked={isVisible}
            onChange={(e) => setIsVisible(e.target.checked)}
            className="w-[18px] h-[18px] accent-dupontGold"
          />
          <span className="text-[14px] font-body text-dupontBlue dark:text-darkText">
            Show my profile in the cousin directory
          </span>
        </label>

        <SharedButton
          onClick={handleSave}
          loadingText={isSaving ? "Saving..." : undefined}
        >
          {existingProfile ? "Update Profile" : "Create Profile"}
        </SharedButton>

        {saved && (
          <div className="text-[14px] text-green-600 font-medium">
            Profile saved!
          </div>
        )}
        {saveError && (
          <div role="alert" className="text-[15px] font-body text-red-700 dark:text-red-400">
            {saveError}
          </div>
        )}
      </div>

      {/* Post-save preview card */}
      {showPreview && (
        <div className="flex flex-col gap-y-[12px]">
          <div className="text-[15px] font-bold text-dupontBlue dark:text-darkText">
            Preview — how your profile looks to others
          </div>
          <div className="max-w-[340px]">
            <ProfileCard profile={previewProfile} />
          </div>
          <button
            type="button"
            className="self-start text-[13px] text-dupontBlue/50 dark:text-darkTextTertiary cursor-pointer underline"
            onClick={() => setShowPreview(false)}
          >
            Hide preview
          </button>
        </div>
      )}

      {/* Crop editor — shows as a modal overlay after uploading a photo */}
      {showCropper && rawImage && (
        <ImageCropper
          imageDataUrl={rawImage}
          onCrop={handleCropComplete}
          onCancel={handleCropCancel}
        />
      )}
    </div>
  );
}

export default MyProfile;
